import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import {
  Trophy, Users, Wallet, Mail, Clock, Calendar,
  AlertTriangle, TrendingUp, ChevronRight, Plus,
  Activity, Bell, UserX, DollarSign, Timer,
  MessageCircle, Cog, PlayCircle, XCircle
} from 'lucide-react';
import './Dashboard.css';

// Skeleton loader for stat cards
function StatCardSkeleton() {
  return (
    <Card className="stat-card-skeleton">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-5 w-32" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Skeleton className="h-8 w-16 mb-1" />
            <Skeleton className="h-3 w-20" />
          </div>
          <div>
            <Skeleton className="h-8 w-16 mb-1" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-0">
        <Skeleton className="h-9 w-full" />
      </CardFooter>
    </Card>
  );
}

// Hero section skeleton
function HeroSkeleton() {
  return (
    <Card className="hero-card bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex-1">
            <Skeleton className="h-4 w-32 mb-2" />
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="flex gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="text-center">
                <Skeleton className="h-10 w-12 mb-1" />
                <Skeleton className="h-3 w-10" />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const { authenticatedFetch, loading: authLoading, isAuthenticated } = useAuth();
  const [stats, setStats] = useState({
    tournaments: { total: 0, active: 0, published: 0, nextTournament: null },
    members: { total: 0, active: 0, suspended: 0, expired: 0 },
    wallets: { totalBalance: 0, activeWallets: 0, totalTransactions: 0 },
    communication: { templates: 0, sentMessages: 0, replies: 0, monitoring: false },
    scheduledEmails: { upcoming: 0, sent: 0, cronRunning: false },
    pace: { avgRoundTime: '--', activePlayers: '--', recentSyncs: '--', totalRounds: '--' },
    cronJobs: { isRunning: false, totalJobs: 0, activeJobs: 0, recentFailures: 0, lastRun: null },
    seasonCalendar: { totalEvents: 0, upcomingEvents: 0, thisMonth: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      loadDashboardStats();
    }
  }, [authLoading, isAuthenticated]);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load tournament stats
      const tournamentsResponse = await authenticatedFetch('/api/tournaments');
      const tournamentsData = await tournamentsResponse.json();
      const tournaments = Array.isArray(tournamentsData) ? tournamentsData : (tournamentsData.tournaments || []);

      // Find next upcoming tournament
      const upcomingTournaments = tournaments
        .filter(t => t.date && new Date(t.date) >= new Date() && !['completed', 'cancelled'].includes(t.status))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      const tournamentStats = {
        total: tournaments.length,
        active: tournaments.filter(t => ['setup', 'processing', 'published-for-feedback'].includes(t.status)).length,
        published: tournaments.filter(t => ['published-for-feedback', 'completed', 'published'].includes(t.status)).length,
        nextTournament: upcomingTournaments[0] || null
      };

      // Load member stats
      const memberStatsResponse = await authenticatedFetch('/api/members/stats');
      const memberStatsData = await memberStatsResponse.json();
      const memberStats = memberStatsData.data || {};

      // Load wallet stats
      let walletStats = { totalBalance: 0, activeWallets: 0, totalTransactions: 0 };
      try {
        const walletStatsResponse = await authenticatedFetch('/api/reports/wallet-summary');
        const walletStatsData = await walletStatsResponse.json();
        const walletData = walletStatsData.data?.statistics || {};
        const totalTransactions = Object.values(walletData.transactionSummary || {})
          .reduce((sum, t) => sum + (t.count || 0), 0);
        const totalWallets = Object.values(walletData.walletCounts || {})
          .reduce((sum, count) => sum + count, 0);

        walletStats = {
          totalBalance: walletData.totalSystemBalance || 0,
          activeWallets: totalWallets,
          totalTransactions: totalTransactions
        };
      } catch (walletErr) {
        console.error('Error loading wallet stats:', walletErr);
      }

      // Load communication stats
      let communicationStats = { templates: 0, sentMessages: 0, replies: 0, monitoring: false };
      try {
        const templatesResponse = await authenticatedFetch('/api/email-templates');
        const templatesData = await templatesResponse.json();

        const dashboardResponse = await authenticatedFetch('/api/communication/dashboard');
        const dashboardData = await dashboardResponse.json();

        const monitoringResponse = await authenticatedFetch('/api/email-monitoring/status');
        const monitoringData = await monitoringResponse.json();

        if (templatesData.success && dashboardData.success) {
          communicationStats = {
            templates: templatesData.count || 0,
            sentMessages: dashboardData.data.sentMessages.total || 0,
            replies: dashboardData.data.replies.total || 0,
            monitoring: monitoringData.success ? monitoringData.data.isRunning : false
          };
        }
      } catch (commErr) {
        console.error('Error loading communication stats:', commErr);
      }

      // Load pace of play stats (lightweight summary endpoint)
      let paceStats = { avgRoundTime: '--', activePlayers: '--', recentSyncs: '--', totalRounds: '--' };
      try {
        const paceResponse = await authenticatedFetch('/api/pace-data/summary');
        const paceData = await paceResponse.json();
        const summary = paceData.data;

        paceStats = {
          avgRoundTime: summary.avgRoundTime || 0,
          activePlayers: summary.activePlayers || 0,
          recentSyncs: summary.recentSyncs || 0,
          totalRounds: summary.totalRounds || 0
        };
      } catch (paceErr) {
        console.error('Error loading pace stats:', paceErr);
      }

      // Load scheduled emails stats
      let scheduledEmailStats = { upcoming: 0, sent: 0, cronRunning: false };
      try {
        // Get upcoming season events with notifications
        const eventsResponse = await authenticatedFetch('/api/season-events?upcoming=true');
        const eventsData = await eventsResponse.json();
        const events = eventsData.events || [];

        // Count upcoming notifications
        const now = new Date();
        let upcomingCount = 0;
        events.forEach(event => {
          if (event.notificationDates) {
            Object.values(event.notificationDates).forEach(data => {
              if (data && data.date && !data.sent && new Date(data.date) > now) {
                upcomingCount++;
              }
            });
          }
        });

        // Get cron status
        const cronResponse = await authenticatedFetch('/api/season-events/cron/status');
        const cronData = await cronResponse.json();

        // Get notification history for sent count
        const historyResponse = await authenticatedFetch('/api/season-events/notifications/history?limit=100');
        const historyData = await historyResponse.json();
        const sentCount = (historyData.notifications || []).filter(n => n.status === 'sent').length;

        scheduledEmailStats = {
          upcoming: upcomingCount,
          sent: sentCount,
          cronRunning: cronData.cronService?.isRunning || false
        };
      } catch (scheduledErr) {
        console.error('Error loading scheduled emails stats:', scheduledErr);
      }

      // Load cron jobs stats
      let cronJobStats = { isRunning: false, totalJobs: 0, activeJobs: 0, recentFailures: 0, lastRun: null };
      try {
        const cronStatusResponse = await authenticatedFetch('/api/cron-jobs/status');
        const cronStatusData = await cronStatusResponse.json();

        // Count recent failures from history
        const recentFailures = (cronStatusData.recentHistory || [])
          .filter(h => h.status === 'error').length;

        // Find most recent run
        const mostRecentRun = cronStatusData.jobs?.reduce((latest, job) => {
          if (!job.lastRun) return latest;
          if (!latest) return job.lastRun;
          return new Date(job.lastRun) > new Date(latest) ? job.lastRun : latest;
        }, null);

        cronJobStats = {
          isRunning: cronStatusData.isRunning || false,
          totalJobs: cronStatusData.jobCount || 0,
          activeJobs: (cronStatusData.jobs || []).filter(j => j.enabled).length,
          recentFailures,
          lastRun: mostRecentRun
        };
      } catch (cronErr) {
        console.error('Error loading cron jobs stats:', cronErr);
      }

      // Load Season Calendar stats
      let seasonCalendarStats = { totalEvents: 0, upcomingEvents: 0, thisMonth: 0 };
      try {
        const calendarResponse = await authenticatedFetch('/api/season-events?season=2026');
        const calendarData = await calendarResponse.json();
        const calendarEvents = calendarData.events || [];

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        const upcomingEvents = calendarEvents.filter(e => new Date(e.eventDate) >= now);
        const thisMonthEvents = calendarEvents.filter(e => {
          const eventDate = new Date(e.eventDate);
          return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
        });

        seasonCalendarStats = {
          totalEvents: calendarEvents.length,
          upcomingEvents: upcomingEvents.length,
          thisMonth: thisMonthEvents.length
        };
      } catch (calErr) {
        console.error('Error loading season calendar stats:', calErr);
      }

      setStats({
        tournaments: tournamentStats,
        members: {
          total: memberStats.totalMembers || 0,
          active: memberStats.activeMembers || 0,
          suspended: memberStats.statusBreakdown?.suspended || 0,
          expired: memberStats.statusBreakdown?.expired || 0
        },
        wallets: walletStats,
        communication: communicationStats,
        pace: paceStats,
        scheduledEmails: scheduledEmailStats,
        cronJobs: cronJobStats,
        seasonCalendar: seasonCalendarStats
      });

    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate countdown to next tournament
  const countdown = useMemo(() => {
    if (!stats.tournaments.nextTournament?.date) return null;

    const tournamentDate = new Date(stats.tournaments.nextTournament.date);
    const now = new Date();
    const diff = tournamentDate - now;

    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, isToday: true };

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return { days, hours, minutes, isToday: days === 0 && hours < 24 };
  }, [stats.tournaments.nextTournament]);

  // Generate alerts based on stats
  const alerts = useMemo(() => {
    const alertList = [];

    if (stats.members.expired > 0) {
      alertList.push({
        type: 'warning',
        title: 'Expired Memberships',
        description: `${stats.members.expired} member${stats.members.expired > 1 ? 's have' : ' has'} expired memberships`,
        link: '/members?status=expired',
        icon: UserX
      });
    }

    if (stats.members.suspended > 0) {
      alertList.push({
        type: 'destructive',
        title: 'Suspended Members',
        description: `${stats.members.suspended} member${stats.members.suspended > 1 ? 's are' : ' is'} currently suspended`,
        link: '/members?status=suspended',
        icon: AlertTriangle
      });
    }

    if (!stats.communication.monitoring) {
      alertList.push({
        type: 'info',
        title: 'Email Monitoring Inactive',
        description: 'Email reply monitoring is currently disabled',
        link: '/communication',
        icon: Mail
      });
    }

    if (stats.tournaments.active === 0 && stats.tournaments.total > 0) {
      alertList.push({
        type: 'info',
        title: 'No Active Tournaments',
        description: 'Consider setting up the next tournament',
        link: '/setup',
        icon: Trophy
      });
    }

    if (!stats.cronJobs.isRunning) {
      alertList.push({
        type: 'destructive',
        title: 'Cron Service Stopped',
        description: 'Scheduled jobs are not running. Check the cron dashboard.',
        link: '/cron-dashboard',
        icon: Cog
      });
    } else if (stats.cronJobs.recentFailures > 0) {
      alertList.push({
        type: 'warning',
        title: 'Cron Job Failures',
        description: `${stats.cronJobs.recentFailures} recent job failure${stats.cronJobs.recentFailures > 1 ? 's' : ''} detected`,
        link: '/cron-dashboard',
        icon: XCircle
      });
    }

    return alertList;
  }, [stats]);

  if (error) {
    return (
      <div className="dashboard p-6">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Dashboard Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadDashboardStats}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="dashboard min-h-screen bg-background p-4 md:p-6">
      {/* Header */}
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">
          Pards Golf Club
        </h1>
        <p className="text-muted-foreground">Tournament & Member Management</p>
      </header>

      {/* Next Tournament Hero Section */}
      {loading ? (
        <HeroSkeleton />
      ) : stats.tournaments.nextTournament ? (
        <Card className="mb-6 bg-gradient-to-r from-primary/10 via-primary/5 to-background border-primary/20 overflow-hidden">
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium text-primary">
                    {countdown?.isToday ? "TODAY!" : "Next Tournament"}
                  </span>
                  {countdown?.isToday && (
                    <Badge variant="success" className="animate-pulse">Live</Badge>
                  )}
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-1">
                  {stats.tournaments.nextTournament.name}
                </h2>
                <p className="text-muted-foreground">
                  {new Date(stats.tournaments.nextTournament.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: 'UTC'
                  })}
                </p>
              </div>

              {countdown && !countdown.isToday && (
                <div className="flex gap-4 md:gap-6">
                  <div className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-primary">{countdown.days}</div>
                    <div className="text-xs text-muted-foreground uppercase">Days</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-primary">{countdown.hours}</div>
                    <div className="text-xs text-muted-foreground uppercase">Hours</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-primary">{countdown.minutes}</div>
                    <div className="text-xs text-muted-foreground uppercase">Mins</div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 w-full lg:w-auto">
                <Button asChild className="flex-1 lg:flex-none">
                  <Link to={`/tournaments/${stats.tournaments.nextTournament._id}`}>
                    View Tournament
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-6 border-dashed">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Trophy className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold">No Upcoming Tournaments</h3>
                  <p className="text-sm text-muted-foreground">Create a new tournament to get started</p>
                </div>
              </div>
              <Button asChild>
                <Link to="/setup">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Tournament
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Stats Cards */}
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tournament Card */}
            {loading ? <StatCardSkeleton /> : (
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Trophy className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg">Tournaments</CardTitle>
                    </div>
                    <Badge variant="secondary">{stats.tournaments.total} total</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-2xl font-bold">{stats.tournaments.active}</div>
                      <div className="text-xs text-muted-foreground">Active</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats.tournaments.published}</div>
                      <div className="text-xs text-muted-foreground">Published</div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-0 gap-2">
                  <Button asChild variant="default" size="sm" className="flex-1">
                    <Link to="/tournaments">Manage</Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="flex-1">
                    <Link to="/setup">
                      <Plus className="h-4 w-4 mr-1" />
                      New
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Members Card */}
            {loading ? <StatCardSkeleton /> : (
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-green-600" />
                      </div>
                      <CardTitle className="text-lg">Members</CardTitle>
                    </div>
                    <Badge variant="secondary">{stats.members.total} total</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div>
                      <div className="text-2xl font-bold text-green-600">{stats.members.active}</div>
                      <div className="text-xs text-muted-foreground">Active</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-yellow-600">{stats.members.expired}</div>
                      <div className="text-xs text-muted-foreground">Expired</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-600">{stats.members.suspended}</div>
                      <div className="text-xs text-muted-foreground">Suspended</div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-0 gap-2">
                  <Button asChild variant="default" size="sm" className="flex-1">
                    <Link to="/members">Manage</Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="flex-1">
                    <Link to="/members?action=create">
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Wallets Card */}
            {loading ? <StatCardSkeleton /> : (
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <Wallet className="h-5 w-5 text-amber-600" />
                      </div>
                      <CardTitle className="text-lg">Wallets</CardTitle>
                    </div>
                    <Badge variant="secondary">{stats.wallets.activeWallets} wallets</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-2xl font-bold text-amber-600">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(stats.wallets.totalBalance)}
                      </div>
                      <div className="text-xs text-muted-foreground">System Balance</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats.wallets.totalTransactions}</div>
                      <div className="text-xs text-muted-foreground">Transactions</div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-0 gap-2">
                  <Button asChild variant="default" size="sm" className="flex-1">
                    <Link to="/wallets">Manage</Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="flex-1">
                    <Link to="/wallets/transfer">
                      <DollarSign className="h-4 w-4 mr-1" />
                      Transfer
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Communication Card */}
            {loading ? <StatCardSkeleton /> : (
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <Mail className="h-5 w-5 text-purple-600" />
                      </div>
                      <CardTitle className="text-lg">Communication</CardTitle>
                    </div>
                    <Badge variant={stats.communication.monitoring ? "success" : "secondary"}>
                      {stats.communication.monitoring ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div>
                      <div className="text-2xl font-bold">{stats.communication.templates}</div>
                      <div className="text-xs text-muted-foreground">Templates</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats.communication.sentMessages}</div>
                      <div className="text-xs text-muted-foreground">Sent</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats.communication.replies}</div>
                      <div className="text-xs text-muted-foreground">Replies</div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-0">
                  <Button asChild variant="default" size="sm" className="w-full">
                    <Link to="/communication">Manage Communication</Link>
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Scheduled Emails Card */}
            {loading ? <StatCardSkeleton /> : (
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-purple-600" />
                      </div>
                      <CardTitle className="text-lg">Scheduled Emails</CardTitle>
                    </div>
                    <Badge variant={stats.scheduledEmails.cronRunning ? "default" : "destructive"}>
                      {stats.scheduledEmails.cronRunning ? '🟢 Running' : '🔴 Stopped'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-2xl font-bold text-purple-600">{stats.scheduledEmails.upcoming}</div>
                      <div className="text-xs text-muted-foreground">Upcoming</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{stats.scheduledEmails.sent}</div>
                      <div className="text-xs text-muted-foreground">Sent</div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-0">
                  <Button asChild variant="default" size="sm" className="w-full">
                    <Link to="/scheduled-emails">View Schedule</Link>
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Cron Jobs Card */}
            {loading ? <StatCardSkeleton /> : (
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-slate-500/10 flex items-center justify-center">
                        <Cog className="h-5 w-5 text-slate-600" />
                      </div>
                      <CardTitle className="text-lg">Cron Jobs</CardTitle>
                    </div>
                    <Badge variant={stats.cronJobs.isRunning ? "success" : "destructive"}>
                      {stats.cronJobs.isRunning ? '🟢 Running' : '🔴 Stopped'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div>
                      <div className="text-2xl font-bold">{stats.cronJobs.totalJobs}</div>
                      <div className="text-xs text-muted-foreground">Total Jobs</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{stats.cronJobs.activeJobs}</div>
                      <div className="text-xs text-muted-foreground">Active</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-600">{stats.cronJobs.recentFailures}</div>
                      <div className="text-xs text-muted-foreground">Failures</div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-0">
                  <Button asChild variant="default" size="sm" className="w-full">
                    <Link to="/cron-dashboard">Manage Cron Jobs</Link>
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Season Calendar Card */}
            {loading ? <StatCardSkeleton /> : (
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-emerald-600" />
                      </div>
                      <CardTitle className="text-lg">Season Calendar</CardTitle>
                    </div>
                    <Badge variant="secondary">2026 Season</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <div className="text-2xl font-bold text-emerald-600">{stats.seasonCalendar.totalEvents}</div>
                      <div className="text-xs text-muted-foreground">Total Events</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{stats.seasonCalendar.upcomingEvents}</div>
                      <div className="text-xs text-muted-foreground">Upcoming</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-amber-600">{stats.seasonCalendar.thisMonth}</div>
                      <div className="text-xs text-muted-foreground">This Month</div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-0">
                  <Button asChild variant="default" size="sm" className="w-full">
                    <Link to="/calendar">View Calendar</Link>
                  </Button>
                </CardFooter>
              </Card>
            )}
          </div>

          {/* Pace of Play Card - Full Width */}
          {loading ? (
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i}>
                      <Skeleton className="h-8 w-16 mb-1" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                      <Timer className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Pace of Play Analytics</CardTitle>
                      <CardDescription>Course performance metrics</CardDescription>
                    </div>
                  </div>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/pace-management">
                      View Details
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-cyan-600">
                      {stats.pace.avgRoundTime !== '--' ? `${stats.pace.avgRoundTime}` : '--'}
                    </div>
                    <div className="text-xs text-muted-foreground">Avg Minutes</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{stats.pace.activePlayers}</div>
                    <div className="text-xs text-muted-foreground">Active Players</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{stats.pace.totalRounds}</div>
                    <div className="text-xs text-muted-foreground">Total Rounds</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{stats.pace.recentSyncs}</div>
                    <div className="text-xs text-muted-foreground">Recent Syncs</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Alerts & Activity */}
        <div className="space-y-6">
          {/* Alerts Section */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">Attention Needed</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <>
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </>
              ) : alerts.length > 0 ? (
                alerts.map((alert, index) => (
                  <Link key={index} to={alert.link} className="block">
                    <Alert variant={alert.type} className="cursor-pointer hover:shadow-sm transition-shadow">
                      <alert.icon className="h-4 w-4" />
                      <AlertTitle className="text-sm">{alert.title}</AlertTitle>
                      <AlertDescription className="text-xs">
                        {alert.description}
                      </AlertDescription>
                    </Alert>
                  </Link>
                ))
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">All clear! No issues need attention.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">Quick Actions</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button asChild variant="outline" size="sm" className="w-full justify-start">
                <Link to="/setup">
                  <Plus className="h-4 w-4 mr-2" />
                  New Tournament
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="w-full justify-start">
                <Link to="/members?action=create">
                  <Users className="h-4 w-4 mr-2" />
                  Add Member
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="w-full justify-start">
                <Link to="/tournaments?status=published-for-feedback">
                  <Activity className="h-4 w-4 mr-2" />
                  Review Feedback
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="w-full justify-start">
                <Link to="/pace-management?action=sync">
                  <Clock className="h-4 w-4 mr-2" />
                  Sync Pace Data
                </Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                asChild
              >
                <a href="https://balboney.sandiego.golf" target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Ask Balboney
                </a>
              </Button>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">System Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Email Monitoring</span>
                  <Badge variant={stats.communication.monitoring ? "success" : "secondary"}>
                    {stats.communication.monitoring ? "Running" : "Stopped"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Cron Service</span>
                  <Badge variant={stats.cronJobs.isRunning ? "success" : "destructive"}>
                    {stats.cronJobs.isRunning ? "Running" : "Stopped"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Active Tournaments</span>
                  <Badge variant={stats.tournaments.active > 0 ? "success" : "secondary"}>
                    {stats.tournaments.active}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Member Status</span>
                  <Badge variant="success">
                    {Math.round((stats.members.active / (stats.members.total || 1)) * 100)}% Active
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
